import React, { MouseEventHandler, ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import importAllFiles from '@support/importAllFiles';
import Section from '@components/Section';
import Contact, { attributesForContactInfo } from '@models/Contact';
import { ContactInfo } from '@app/types';
import { CheckIcon, PlusIcon, XIcon } from '@components/Icons';
import { startNewContact } from '@actions/ui';
import { useTheme } from '@hooks/use-theme';
import SourceConflictModal from '../SourceConflictModal';
import styles from './SourcesListEditor.module.scss';
import CopySourceBtn from '../CopySourceBtn';
import { selectContactSources } from '@reducers/data/sources';
import { createSource, deleteSource } from '@actions/sources';

const icons = importAllFiles(import.meta.glob('/public/img/contact/icons/source/*.svg'));

const confirmAddingDeletedSource = ({
  source,
  addSourceToContact,
  createNewContactFromSource,
}: {
  source: string;
  addSourceToContact: () => void;
  createNewContactFromSource: () => void;
}) => {
  window.app.confirm(
    {
      title: 'Looks like you removed this source from someone else.',
      message: 'Would you like to start with a blank contact?',
      actions: [
        { title: 'New Contact', value: 'newContact', variant: 'primary' },
        {
          title: `Add "${source}"`,
          value: 'useClear',
          variant: 'primary',
          autoFocus: true,
        },
      ],
      smallModal: false,
    },
    (action?: 'useClear' | 'newContact') => {
      if (action && action == 'useClear' && source) {
        addSourceToContact();
      } else if (action && action == 'newContact') {
        createNewContactFromSource();
      }
    },
  );
};

function IconBtn({ onClick, children }) {
  return (
    <button tabIndex={0} className={styles.iconBtn} onClick={onClick}>
      {children}
    </button>
  );
}

function ActionBtn({
  disabled,
  onClick,
  children,
}: {
  disabled?: boolean;
  onClick: MouseEventHandler;
  children: ReactNode;
}) {
  return (
    <button disabled={disabled} className={styles.actionBtn} onClick={onClick}>
      {children}
    </button>
  );
}

function EditableSourceIcon({ source }: { source: ContactInfo }) {
  const [theme] = useTheme();
  let iconKey = source.type;

  if (!['twitter', 'linkedin', 'email', 'phone', 'manual', 'facebook'].includes(iconKey)) {
    return null;
  }

  const icon = icons[`${iconKey}_${theme}`] || icons[iconKey];

  return (
    <span
      className={cn(styles.icon, styles.editableSourceIcon)}
      style={{ backgroundImage: `url(${icon})` }}
    />
  );
}

export function SourcesListEditor({ contact }: { contact: Contact }) {
  const dispatch = useDispatch();
  const sources = useSelector(selectContactSources(contact.id));
  const sourcesList = sources.items.valueSeq().toArray();
  const [theme] = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [newSourceValue, setNewSourceValue] = useState('');
  const [sourceConflictModalOpen, setSourceConflictModalOpen] = useState(false);

  const didMount = useRef(false);
  // a contact needs to have at least one source
  const canRemoveSources = sourcesList.length > 1;

  // Handle 409 response by prompting user
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    if (sources.error?.status !== 409) {
      return;
    }

    if (sources.error.response?.contact_id) {
      setSourceConflictModalOpen(true);
    } else {
      confirmAddingDeletedSource({
        source: sources.error.requestValue,
        addSourceToContact: () => {
          dispatch(
            createSource({
              contactID: contact.id,
              value: sources.error.requestValue,
              clearExisting: true,
            }),
          );
        },
        createNewContactFromSource: () => {
          dispatch(startNewContact(new Contact({ displayName: sources.error.requestValue || '' })));
        },
      });
    }
  }, [sources.error]);

  const startAddingSource = (e) => {
    setIsEditing(true);
    e.preventDefault();
  };

  const clearCurrentlyEditingSource = () => {
    setIsEditing(false);
    setNewSourceValue('');
  };

  const addSource = () => {
    if (newSourceValue != '') {
      dispatch(
        createSource({
          contactID: contact.id,
          value: newSourceValue,
          clearExisting: false,
        }),
      );
    }

    clearCurrentlyEditingSource();
  };

  const confirmDelete = (source: ContactInfo) => {
    window.app.confirm(
      {
        title: 'Removing Source',
        message: (
          <>
            Are you sure you want to remove <b>{source.value}</b> from this profile?
          </>
        ),
        action: 'Remove',
        variant: 'danger',
      },
      (confirmed: boolean) => {
        if (confirmed) {
          dispatch(deleteSource({ contactID: contact.id, source }));
        }
      },
    );
  };

  const handleInputKeyDown = (e) => {
    // "Enter"
    if (e.keyCode == 13) {
      e.preventDefault();
      addSource();
    }

    // "Esc"
    if (e.keyCode == 27) {
      e.preventDefault();
      clearCurrentlyEditingSource();
    }
  };

  // We are preventDefault'ing somewhere higher up. This lets the user do tab nav in this section.
  const stopTabPropagation = (e) => {
    if (e.keyCode == 9) {
      e.stopPropagation();
    }
  };

  return (
    <>
      <Section onKeyDown={stopTabPropagation}>
        <Section.Title underline>Sources</Section.Title>
        {sourcesList.map((source, idx) => {
          const { href } = attributesForContactInfo(source, false);
          const copyValue = ['email', 'phone', 'messages'].includes(source.type)
            ? source.value
            : href;

          return (
            <div
              className={cn(
                styles.source,
                styles.removeableSource,
                !canRemoveSources && styles.disabledSource,
              )}
              key={idx}
            >
              <EditableSourceIcon source={source} />
              <span className={styles.sourceValue}>{source.value}</span>
              <CopySourceBtn value={copyValue} className={styles.copyBtn} />
              {canRemoveSources && (
                <IconBtn
                  onClick={(e) => {
                    e.preventDefault();
                    confirmDelete(source);
                  }}
                >
                  <XIcon width="9" />
                </IconBtn>
              )}
            </div>
          );
        })}
        {isEditing ? (
          <div className={styles.inputContainer}>
            <div className={styles.placeholder}>
              {['email', 'twitter', 'linkedin', 'phone'].map((iconKey) => (
                <span
                  key={iconKey}
                  style={{
                    backgroundImage: `url(${icons[`${iconKey}_${theme}`] || icons[iconKey]})`,
                  }}
                />
              ))}
            </div>
            <input
              type="text"
              value={newSourceValue}
              placeholder="Enter Contact"
              onChange={(e) => setNewSourceValue(e.target.value)}
              autoFocus
              spellCheck={false}
              onKeyDown={handleInputKeyDown}
            />
            <IconBtn onClick={addSource}>
              <CheckIcon width="10" />
            </IconBtn>
            <IconBtn onClick={clearCurrentlyEditingSource}>
              <XIcon width="9" />
            </IconBtn>
          </div>
        ) : (
          <ActionBtn onClick={startAddingSource}>
            Add Source
            <PlusIcon width={8} height={8} />
          </ActionBtn>
        )}
      </Section>
      {sourceConflictModalOpen && (
        <SourceConflictModal
          contact={contact}
          error={sources.error}
          closeModal={() => setSourceConflictModalOpen(false)}
        />
      )}
    </>
  );
}
